/**
 * Import into the list on screen — paste, drop or choose a file, see what will
 * happen, then add. `docs/features/tracklists.md`, *Import, paste, export*.
 *
 * **Nothing is filed until the button is pressed**, and the button says how
 * many. Every change to the box re-plans at once (`planImport` is pure), so the
 * three piles — will be added, already in, refused — are always the answer for
 * what is in the box now. Paste from clipboard opens this with the box filled,
 * for the same reason: a pasted list is deliberate, and what it would file has
 * to be on screen first.
 *
 * A refusal for a symbol the index does not hold is reworded by `explain` —
 * *not in your plan* or *isn't a symbol* — the Add row's sentences, asked for
 * only for what is on screen.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import type { SymbolRow } from "../lib/symbol-index";
import { refusalFor } from "../lib/symbol-access";
import {
  fileImport,
  planImport,
  readImport,
  type ImportSource,
} from "../lib/track-list-import";
import type { TrackList } from "../lib/track-lists";

export interface TrackListImportDialogProps {
  list: TrackList;
  /** The symbol index, or undefined while it loads — nothing is checkable until it lands. */
  rows: readonly SymbolRow[] | undefined;
  /** The box's first contents — the clipboard, for Paste from clipboard. */
  initialText?: string;
  add: (listId: string, symbols: readonly string[], closes: Record<string, number>) => Promise<void>;
  /** Something went in: ask for the screen again. */
  onAdded: () => void;
  onClose: () => void;
  explain?: (value: string, leg: string) => Promise<string>;
}

/** How many symbols a pile shows before `show all`. */
const PREVIEW = 24;

export function TrackListImportDialog({
  list,
  rows,
  initialText = "",
  add,
  onAdded,
  onClose,
  explain = refusalFor,
}: TrackListImportDialogProps) {
  const [text, setText] = useState(initialText);
  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  /** `refusalFor`'s sentences, by the value they explain. */
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const ref = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const source: ImportSource = useMemo(() => readImport(text, fileName), [text, fileName]);
  const plan = useMemo(() => planImport(source, { rows, list }), [source, rows, list]);

  // Reword each unheld refusal once. Keyed by value, so retyping the box does not
  // ask again for a symbol already explained.
  useEffect(() => {
    let live = true;
    const pending = plan.refused.filter((r) => r.outside && !(r.outside.value in reasons));
    if (pending.length === 0) return;
    void Promise.all(
      pending.map(async (r) => [r.outside!.value, await explain(r.outside!.value, r.outside!.leg)] as const),
    ).then((pairs) => {
      if (!live) return;
      setReasons((held) => ({ ...held, ...Object.fromEntries(pairs) }));
    });
    return () => {
      live = false;
    };
  }, [plan, reasons, explain]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const readFile = (file: File) => {
    void file.text().then((body) => {
      setFileName(file.name);
      setText(body);
      setDone(null);
      setError(null);
    });
  };

  const submit = async () => {
    if (busy || plan.add.length === 0) return;
    setBusy(true);
    setError(null);
    let added = 0;
    try {
      added = await fileImport(plan, list.id, add);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setBusy(false);
    if (added > 0) {
      onAdded();
      setDone(added);
    }
  };

  const pile = (key: string, items: string[]) => {
    const all = open[key] || items.length <= PREVIEW;
    return (
      <span style={SYMS}>
        {(all ? items : items.slice(0, PREVIEW)).join(" ")}
        {all ? null : (
          <button style={LINK} onClick={() => setOpen((o) => ({ ...o, [key]: true }))}>
            {" "}+{items.length - PREVIEW} more
          </button>
        )}
      </span>
    );
  };

  const refusedLines = plan.refused.map((r) => ({
    text: r.text.toUpperCase(),
    reason: (r.outside && reasons[r.outside.value]) || r.reason,
  }));

  return (
    <div style={SCRIM}>
      <div
        ref={ref}
        role="dialog"
        aria-label={`Import into ${list.name}`}
        style={PANEL}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files[0];
          if (file) readFile(file);
        }}
      >
        <h4 style={TITLE}>Import into {list.name}</h4>

        <textarea
          autoFocus
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setFileName(undefined);
            setDone(null);
          }}
          placeholder="Paste symbols, or drop a .txt / .csv file here"
          spellCheck={false}
          style={AREA}
          aria-label="Symbols to import"
        />

        <div style={{ ...ROW, marginTop: 6 }}>
          <button style={BTN} onClick={() => fileInput.current?.click()}>
            Choose file…
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".txt,.csv,.tsv,text/plain,text/csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) readFile(file);
              e.target.value = "";
            }}
          />
          <span style={HINT}>
            {fileName
              ? `${fileName}${source.table ? " — first column" : ""}`
              : "spaces or commas · one per line · first column of a CSV"}
          </span>
        </div>

        {rows === undefined ? (
          <p style={NOTE}>Loading the symbol list…</p>
        ) : plan.found > 0 ? (
          <div style={{ marginTop: 10 }}>
            <p style={{ ...NOTE, marginTop: 0 }}>
              {plan.found} found · {plan.add.length} will be added · {plan.already.length} already in ·{" "}
              {plan.refused.length} refused
            </p>
            {plan.add.length > 0 ? (
              <div style={PILE}>
                <span style={{ ...MARK, color: "var(--qf-positive)" }}>✓ Add</span>
                {pile("add", plan.add)}
              </div>
            ) : null}
            {plan.already.length > 0 ? (
              <div style={PILE}>
                <span style={MARK}>= Already in</span>
                {pile("already", plan.already)}
              </div>
            ) : null}
            {refusedLines.length > 0 ? (
              <div style={PILE}>
                <span style={{ ...MARK, color: "var(--qf-negative)" }}>✗ Refused</span>
                <div style={REFUSED}>
                  {(open.refused ? refusedLines : refusedLines.slice(0, PREVIEW)).map((r, i) => (
                    <div key={`${r.text}-${i}`}>
                      <b>{r.text}</b> <span style={{ color: "var(--qf-text-muted)" }}>{r.reason}</span>
                    </div>
                  ))}
                  {!open.refused && refusedLines.length > PREVIEW ? (
                    <button style={LINK} onClick={() => setOpen((o) => ({ ...o, refused: true }))}>
                      +{refusedLines.length - PREVIEW} more
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? <p style={ERROR}>{error}</p> : null}
        {done !== null ? (
          <p style={{ ...NOTE, color: "var(--qf-positive)" }}>
            {done === 1 ? "1 symbol" : `${done} symbols`} added to {list.name}.
          </p>
        ) : null}

        <div style={FOOT}>
          {refusedLines.length > 0 ? (
            <button
              style={{ ...BTN, marginRight: "auto" }}
              onClick={() => void navigator.clipboard?.writeText(refusedLines.map((r) => r.text).join("\n"))}
            >
              Copy refused
            </button>
          ) : null}
          <button style={BTN} onClick={onClose}>
            {done !== null ? "Close" : "Cancel"}
          </button>
          <button
            style={{ ...BTN, ...PRIMARY }}
            onClick={() => void submit()}
            disabled={busy || plan.add.length === 0 || done !== null}
          >
            {busy ? "Adding…" : `Add ${plan.add.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const SCRIM: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,.35)",
};

const PANEL: React.CSSProperties = {
  width: "min(520px, calc(100vw - 32px))",
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
  padding: "14px 16px 16px",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  boxShadow: "0 14px 40px -12px rgba(0,0,0,.6)",
  color: "var(--qf-text)",
  fontSize: 12,
};

const TITLE: React.CSSProperties = { margin: "0 0 10px", fontSize: 13, fontWeight: 600 };

const AREA: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 96,
  resize: "vertical",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 3,
  padding: "6px 7px",
  color: "var(--qf-text)",
  font: "inherit",
  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
  fontSize: 11.5,
};

const ROW: React.CSSProperties = { display: "flex", alignItems: "center", gap: 9 };

const HINT: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 10.5, minWidth: 0 };

const NOTE: React.CSSProperties = { margin: "10px 0 0", fontSize: 11, color: "var(--qf-text-muted)" };

const PILE: React.CSSProperties = { display: "flex", gap: 8, margin: "6px 0 0", alignItems: "baseline" };

const MARK: React.CSSProperties = { width: 78, flex: "none", fontSize: 11, color: "var(--qf-text-muted)" };

const SYMS: React.CSSProperties = {
  fontFamily: "ui-monospace, Menlo, Consolas, monospace",
  fontSize: 11,
  wordBreak: "break-word",
  minWidth: 0,
};

const REFUSED: React.CSSProperties = { ...SYMS, display: "flex", flexDirection: "column", gap: 2 };

const ERROR: React.CSSProperties = { margin: "10px 0 0", fontSize: 11, color: "var(--qf-negative)" };

const FOOT: React.CSSProperties = { display: "flex", justifyContent: "flex-end", gap: 7, marginTop: 14 };

const BTN: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
  borderRadius: 3,
  padding: "4px 10px",
  font: "inherit",
  fontSize: 11.5,
  cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};

const LINK: React.CSSProperties = {
  border: "none",
  background: "none",
  padding: 0,
  color: "var(--qf-accent)",
  font: "inherit",
  fontSize: 11,
  cursor: "pointer",
};
